<script setup lang="ts">
import { computed, ref } from 'vue';
import Tooltip from './Tooltip.vue';
import TooltipContent from './TooltipContent.vue';
import TooltipTrigger from './TooltipTrigger.vue';
import { useLazyTooltip } from '@/composables/useLazyTooltip';

/**
 * A tooltip that costs nothing until it is hovered.
 *
 * Renders only its trigger content by default and mounts the reka-ui Tooltip
 * stack on the first hover or keyboard focus. Use it anywhere a tooltip is
 * rendered once per row of a long list.
 *
 * The reason is not theoretical: a track card at `full` density carries four
 * tooltips, and a bucket resolves 500 tracks, so switching density used to
 * mount two thousand tooltips in one synchronous pass and the page stopped
 * responding. Unlike OverflowTooltip, this one opens whether or not the
 * trigger is truncated, because its text ("DB Bitrate", a full timestamp) is
 * meaningful either way.
 *
 * The swap itself, and the ways it can strand a tooltip on screen, are handled
 * by {@link useLazyTooltip}, the same path OverflowTooltip uses.
 *
 * On a menu or popover button, wrap the whole menu, not just its trigger:
 *
 *     <LazyTooltip>
 *       <DropdownMenu>
 *         <DropdownMenuTrigger as-child><button … /></DropdownMenuTrigger>
 *         <DropdownMenuContent>…</DropdownMenuContent>
 *       </DropdownMenu>
 *       <template #content>More actions</template>
 *     </LazyTooltip>
 *
 * The reason is how reka places floating things. A tooltip and a menu both
 * keep their anchor in one shared reka context, and a trigger registers with
 * the nearest one above it. With the tooltip between the menu and its trigger,
 * the menu's button registered with the tooltip instead. The menu was left
 * with no anchor and opened in the window's top-left corner. The same is true
 * of the plain Tooltip. `tooltipMenuNesting.test.ts` checks every component.
 *
 * The other way out is to keep the popover's root outside the tooltip and hang
 * the popover from a `PopoverAnchor` that is outside the tooltip too. The Sync
 * pill does this, because its popover must open at the pill.
 */
const props = withDefaults(
    defineProps<{
        side?: 'top' | 'right' | 'bottom' | 'left';
        align?: 'start' | 'center' | 'end';
        /** Passed through to TooltipContent, for tooltips that lay out their own body. */
        contentClass?: string;
        /**
         * Whether this tooltip may open at all. `false` renders the slot and
         * nothing else. Use it for a trigger that needs a tooltip in only some
         * of its states, such as a toolbar button whose label is on screen
         * until the toolbar runs out of room and drops it.
         */
        enabled?: boolean;
    }>(),
    { side: 'top', align: 'center', contentClass: undefined, enabled: true },
);

const isOpen = ref(false);
const triggerRef = ref<HTMLElement | null>(null);

/**
 * What the tooltip positions itself against. The trigger wrapper is
 * `display: contents` and has no box, so measured on its own it sits at 0,0
 * and the tooltip was drawn in the window's top-left corner. The first real
 * element inside it has the box the reader is pointing at.
 */
const anchorRef = computed(() => (triggerRef.value?.firstElementChild as HTMLElement | null) ?? undefined);

const { activated, probeRef, onProbePointerOver, onProbePointerOut, onProbeFocusIn, onProbeFocusOut, onTriggerFocusIn, onTriggerLeave } =
    useLazyTooltip({
        open: () => {
            isOpen.value = true;
        },
        close: () => {
            isOpen.value = false;
        },
        triggerElement: () => triggerRef.value,
        canActivate: () => props.enabled,
    });
</script>

<template>
    <!--
        `display: contents` so this wrapper adds no box and the trigger keeps the
        exact layout it has once the Tooltip takes over. `pointerover`, not
        `pointerenter`: an element with no box is never the target itself, so the
        handler only ever runs on an event bubbling up from the slot content —
        and `pointerenter` does not bubble. `pointerout` is its bubbling pair.
    -->
    <span
        v-if="!activated || !enabled"
        ref="probeRef"
        class="contents"
        @pointerover="onProbePointerOver"
        @pointerout="onProbePointerOut"
        @focusin="onProbeFocusIn"
        @focusout="onProbeFocusOut"
    >
        <slot />
    </span>
    <Tooltip v-else :open="isOpen" @update:open="isOpen = $event">
        <TooltipTrigger as-child :reference="anchorRef">
            <span ref="triggerRef" class="contents" @pointerout="onTriggerLeave" @focusin="onTriggerFocusIn" @focusout="onTriggerLeave">
                <slot />
            </span>
        </TooltipTrigger>
        <TooltipContent :side="side" :align="align" :class="contentClass">
            <slot name="content" />
        </TooltipContent>
    </Tooltip>
</template>
